import axios from 'axios'

class DocumentsType {

  static #apiUrl = process.env.REACT_APP_MIFIRMA_API
  constructor(apiUrl) {
    this.apiUrl = apiUrl
  }

  /**
  * @author Nestor Sandoval
  * @description GET a type of documents
  * @date 22-05-2025
  * @param {String} companyId
  * @param {String} categoryId
  * @param {String} token
  * @returns {Object}
  * @memberof CategoryService
  */
  async getAllTypeDocuments(token) {
    let response = await axios.get(`${this.apiUrl}/vault-document-types`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
    return response.data
  }

  /**
  * @author Nestor Sandoval
  * @description POST a type of document
  * @date 22-05-2025
  * @param {String} name
  * @param {String} infoType
  * @param {String} description
  * @param {String} token
  * @returns {Object}
  */
  async postTypeDocument(name, infoType, description, dataProfile, token) {
    let body = { name, infoType, description }
    if (dataProfile && dataProfile !== '') body.dataProfile = dataProfile
    const response = await axios.post(
      `${this.apiUrl}/vault-document-types`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    return response.data
  }

  /**
  * @author Nestor Sandoval
  * @description PUT a type of document
  * @date 22-05-2025
  * @param {String} name
  * @param {String} infoType
  * @param {String} description
  * @param {String} token
  * @returns {Object}
  */
  async putTypeDocument(id, name, infoType, description, dataProfile, token) {
    let body = { id, name, infoType, description }
    if (dataProfile && dataProfile !== '') body.dataProfile = dataProfile

    const response = await axios.put(
      `${this.apiUrl}/vault-document-types`,
      body,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    return response.data
  }

  /**
  * @author Nestor Sandoval
  * @description DELETE a type of document
  * @date 22-05-2025
  * @param {String} id
  * @param {String} token
  * @returns {Object}
  */
  async deleteTypeDocument(id, token) {
    const response = await axios.delete(
      `${this.apiUrl}/vault-document-types?id=${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    return response.data
  }

  /**
   * @author Edgar Escobedo
   * @description Create or update variable
   * @createdDate 11-08-2025
   * @typedef {Object} Props
   * @property {Number} documentId ID of the document the variable will be related to
   * @property {Number} variableId ID  variable to update
   * @property {Number} typeDocumentId ID of the document the variable will be related to
   * @property {String} variableName Name of variable
   * @property {String | Number | Date | Boolean} valueVariable Value of variable
   * @property {String} typeData (e.g., 'text', 'number', 'date', 'boolean', 'selection')
   * @property {String | null} formatVariableKey Format key associated with the formatVariable table
   * @property {String | null} dataProfile The data profile selected (e.g., 'contract', 'proofOfAddress', 'ids').
   * @property {Boolean} mandatory The variable is mandatory
   * @property {String} token
   * @param {Props}
   * @returns {Promise<{message: String, data: {} | null, statusCode: Number}>}
   * @memberof DocumentsType
   */
  async createOrUpdateVariable({
    variableId,
    typeDocumentId,
    variableName,
    typeData,
    formatVariableKey,
    dataProfile,
    mandatory,
    documentId,
    valueVariable,
    entityType,
    token
  }) {
    const body = { typeDocumentId, variableName, typeData, mandatory }

    if (variableId != null) body.variableId = variableId
    if (formatVariableKey != null) body.formatVariableKey = formatVariableKey
    if (dataProfile != null) body.dataProfile = dataProfile
    if (documentId != null) body.documentId = documentId
    if (valueVariable != null) body.valueVariable = valueVariable
    if (entityType != null) body.entityType = entityType

    const response = await axios.post(`${this.apiUrl}/variable`, body, {
      headers: { Authorization: `Bearer ${token}` }
    })
    return response.data
  }

  /**
   * @author Edgar Escobedo
   * @description Delete variable
   * @createdDate 11-08-2025
   * @typedef {Object} Props
   * @property {Number} variableId ID variable to delete
   * @property {String} token
   * @param {Props}
   * @returns {{message: String, data: boolean | null, statusCode: Number}}
   * @memberof DocumentsType
   */
  async deleteVariable({ variableId, token }) {
    const response = await axios.delete(`${this.apiUrl}/variable`, {
      params: { variableId },
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
    return response.data
  }


  async deleteVariableValue({ variableId, token, documentId }) {
    const response = await axios.delete(`${this.apiUrl}/value-variable`, {
      params: { variableId, documentId },
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
    return response.data
  }

  /**
   * @author Pedro Antonio
   * @param {string} documentId
   * @param {string} token
   * @return {Promise<import("../types/api.types").ApiResponse<import("../types/document-type.types").DocumentTypeResponse>>} 
   * @memberof DocumentsType
   */
  static async getTypesByDocumentId(documentId, token) {
    const response = await axios.get(`${this.#apiUrl}/vault-document-type`, {
      params: { documentId },
      headers: {
        Authorization: `Bearer ${token}`,
      }
    })
    return response.data
  }


}

export { DocumentsType as default }